import { useEffect, type ReactNode } from 'react'
import { X } from '@phosphor-icons/react'

type Props = {
  open: boolean
  onClose: () => void
  title: string
  subtitle?: string
  children: ReactNode
  footer?: ReactNode
  /** Ancho máximo. 'sm'=420, 'md'=600 (default), 'lg'=820 */
  size?: 'sm' | 'md' | 'lg'
  /** Si true, no se cierra al click fuera (forzar uso de Cancelar) */
  blocking?: boolean
}

const SIZES: Record<NonNullable<Props['size']>, number> = {
  sm: 420,
  md: 600,
  lg: 820,
}

/**
 * Modal genérico — overlay + card centrado.
 * - Esc cierra (a menos que `blocking`)
 * - Click fuera cierra (a menos que `blocking`)
 * - Bloquea scroll del body mientras está abierto
 */
export function Modal({
  open,
  onClose,
  title,
  subtitle,
  children,
  footer,
  size = 'md',
  blocking = false,
}: Props) {
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !blocking) onClose()
    }
    document.addEventListener('keydown', onKey)
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = ''
    }
  }, [open, blocking, onClose])

  if (!open) return null

  return (
    <div
      className="modal-overlay"
      onClick={blocking ? undefined : onClose}
      role="presentation"
    >
      <div
        className="modal-card"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        style={{ maxWidth: SIZES[size] }}
      >
        <div className="modal-header">
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 id="modal-title" className="modal-title">{title}</h2>
            {subtitle && <p className="modal-subtitle">{subtitle}</p>}
          </div>
          <button
            type="button"
            className="modal-close"
            onClick={onClose}
            aria-label="Cerrar"
          >
            <X size={16} weight="bold" />
          </button>
        </div>

        <div className="modal-body">
          {children}
        </div>

        {footer && (
          <div className="modal-footer">
            {footer}
          </div>
        )}
      </div>
    </div>
  )
}
